import { inject, Injectable, isDevMode, NgZone } from '@angular/core'
import {
  BehaviorSubject,
  Observable,
  asyncScheduler,
  catchError,
  combineLatest,
  from,
  map,
  merge,
  mergeMap,
  observeOn,
  of,
  subscribeOn,
  switchMap,
  take,
  takeWhile,
  tap,
  toArray
} from 'rxjs'

import {
  CollectionReference,
  Firestore,
  Query,
  Timestamp,
  collection,
  collectionSnapshots,
  doc,
  docData,
  documentId,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from '@angular/fire/firestore'

import { FirebaseError } from '@angular/fire/app'

import {
  DateOnly,
  datelib,
  getRandomInt,
  sortByArray,
  toNonNullableArray,
  toUniqueNonNullableArray
} from '@nx-superprep/utils'

//workaround for dayjs esm issue
import isSameOrBefore from 'dayjs/esm/plugin/isSameOrBefore'
datelib.extend(isSameOrBefore)

import { StorageService } from './storage.service'
import { UserService } from './user.service'
import { StudyService } from './study.service'

import {
  CardTemplate,
  CardStats,
  DeckMode,
  StudyPhase,
  StudyOrderProgress,
  UserResponse,
  AcquisitionCardRetrievalRequest,
  PracticeCardRetrievalRequest,
  ReviewCardRetrievalRequest,
  IncludedModuleInfo,
} from '../models/back-end'

import { Choice, FrontendCard, FrontendCardTemplate, Resource } from '../models/front-end'

import { log } from '@nx-superprep/utils'
import { chunk, isEqual, uniqBy } from 'lodash-es'
import UINT16_MAX from '@stdlib/constants-uint16-max'
import { mapToEntities } from "./map-to-entities"

export interface CardRequest extends AcquisitionCardRetrievalRequest, ReviewCardRetrievalRequest, PracticeCardRetrievalRequest {
  moduleId: string,
  studyPhase: StudyPhase,
  planDate?: string,
  maxCardCount: number,
  deckMode: DeckMode,
  studyOrderProgress?: StudyOrderProgress,
}

@Injectable({ providedIn: 'root' })
export class CardService {
  readonly zone = inject(NgZone)
  readonly studyService = inject(StudyService)
  readonly storageService = inject(StorageService)
  readonly firestore = inject(Firestore)
  readonly userService = inject(UserService)

  selectedItem = new BehaviorSubject<FrontendCard | undefined>( undefined )

  previousSelectedCard: FrontendCard | undefined

  constructor() {
    this.userService.userData.subscribe(() => this.clear())
  }

  // track locally cached modules
  private readonly _cachedModuleInfoMap = new Map<string, IncludedModuleInfo>()
  get cachedModuleInfo() {
    return Array.from(this._cachedModuleInfoMap.values())
  }
  // local cache for front end cards
  private readonly _cardsMap = new Map<string, FrontendCard>()
  get cards() {
    return Array.from(this._cardsMap.values())
  }

  get selectedCard() {
    return this.selectedItem.value
  }
  set selectedCard(value) {
    this.previousSelectedCard = this.selectedCard
    this.selectedItem.next(value)
  }

  clear() {
    this._cardsMap.clear()
    this._cachedModuleInfoMap.clear()
    this.selectedCard = undefined
    this.previousSelectedCard = undefined
  }

  //-----------------------------------------------------------------------------------------------------
  //  cache operations
  //-----------------------------------------------------------------------------------------------------

  cacheAddModule(id: string | undefined, concurrent?: number, onProgress?: (completed: number, total: number) => void) {
    let result = of({cards:[] as FrontendCard[]|FrontendCardTemplate[], resources:[] as Resource[]})
    if (!id) { return result }
    if (!navigator.onLine) { log.debug(`offline not loading cards for ${id}`); return result }
    const found = this._cachedModuleInfoMap.get(id)
    if (found && new DateOnly(found.includedDate).isToday()) { log.debug(`already fetched cards for ${id}`); return result }
    const chunkSize = 30
    log.debug(`fetching all cards for ${id} in batches of ${chunkSize}`)
    result = this.fetchFrontendCards({ moduleId: id, templateOnly: false, chunkSize })
      .pipe(
        take(1),
        switchMap((cards) => {
          log.debug(`fetched ${cards.length} cards for ${id}`)
          return from(this.assetizeCards(cards, true, concurrent, onProgress)).pipe(
            // Process in background with lower priority
            subscribeOn(asyncScheduler),
            // Handle errors silently to prevent UI disruption
            catchError(error => {
              log.debug('Background caching failed:', error);
              return of({ cards: [], resources: [] });
            }),
            // Prevent this from blocking main thread
            observeOn(asyncScheduler),
            tap((rs) => {
              this._cachedModuleInfoMap.set(id, new IncludedModuleInfo(id, DateOnly.toTimestamp()))
              log.debug(`assetized ${rs.cards.length} cards for ${id}`)
              this.zone.run(() => {
                rs.cards.forEach(c => this._upsert(c))
              })
              //this.backendService.saveAssets(id, rs.resources.filter(r => !!r.url).map(r => r.url))
            })
          )
        }),
        tap((rs) => {
          const _resources = (rs.resources).filter((r) => ['audio', 'image', 'video'].includes(r.assetType ?? ''))
          const resources = uniqBy(_resources, (r) => `${r.name}-${r.assetType}`)
          log.debug(`loaded ${resources.length} unique resources`)
        })
      )
    return result
  }

  cacheRemoveModule(id: string | null | undefined) {
    if (!id) { return }
    this._cachedModuleInfoMap.delete(id)
    this.cards.forEach((c) => { if (c.moduleId === id) {this._cardsMap.delete(c.id)}})
  }

  updateCache(
    addIds: string[] = [],
    removeIds: string[] = [],
    includedModuleInfos: IncludedModuleInfo[] = this.studyService.IncludedModuleInfo
  ) {
    removeIds.push(...this.cachedModuleInfo.filter((c) => !includedModuleInfos.find((m) => m.moduleId === c.moduleId)).map((m) => m.moduleId))
    if (addIds.length === 0) {
      addIds = toUniqueNonNullableArray(
        includedModuleInfos.map((m) => {
          const found = this._cachedModuleInfoMap.get(m.moduleId)
          return found
            ? new DateOnly(found.includedDate).isBefore(new DateOnly(m.includedDate)) ? m.moduleId : undefined
            : m.moduleId
        })
      )
    }
    return [removeIds.map((id) => this.cacheRemoveModule(id)), addIds.map((id) => this.cacheAddModule(id))]
  }

  getCachedCards(
    request: CardRequest,
    options?: { ids: string[] },
    cards = this.cards
  ) {
    const result = this.filterByModuleId(cards, request.moduleId)
    return this._applyStudyPhaseFilter(result.slice(0, request.cardCount), request, options)
  }

  private _applyStudyPhaseFilter(cards: FrontendCard[], request: CardRequest, options?: { ids: string[] }): FrontendCard[] {
    switch (request.studyPhase) {
      case StudyPhase.Acquisition: {
        return (options?.ids ?? []).length > 0
          ? cards.filter((c) => c.score < 1 && options?.ids.includes(c.id))
          : cards.filter((c) => c.score < 1)
      }
      case StudyPhase.Review: {
        const afterReviewDueDate = new DateOnly(request.afterReviewDueDateString)
        return (options?.ids ?? []).length > 0
          ? cards.filter((c) => options?.ids.includes(c.id))
          : cards.filter((c) => c.reviewDueDate && afterReviewDueDate.isSameOrBefore(c.reviewDueDate))
      }
      case StudyPhase.Quiz: {
        return cards
      }
      case StudyPhase.Practice: {
        const found = request.afterCardId ? cards.findIndex((c) => c.id === request.afterCardId) : 0
        return found > 0 ? cards.slice(found) : cards
      }
      default:
        return []
    }
  }

  private _upsert(item: FrontendCard | CardTemplate | undefined) {
    if (!item?.id) {
      return
    }
    const previous = this._cardsMap.get(item.id)
    const current = this.toFrontendCard(item)
    const didChange = navigator.onLine && !(previous && !isEqual(current, previous))
    if (didChange) { this._cardsMap.set(item.id, current) }
    return didChange ? current : previous
  }

  // background pre-fetching
  backgroundPrefetch(moduleId: string|undefined, isWeb: boolean, onProgress: (completed: number, total: number) => void) {
    if (!moduleId || !navigator.onLine) { return }
    if (!isWeb || isDevMode()) { return }
    return
    // Pre-fetch next module in background
    //const nextModuleId = this.studyService.getNextModuleId(this.moduleId)
    //this.cardService.cacheAddModule(nextModuleId, 2).subscribe()

    // Pre-cache current module assets
    this.cacheAddModule(moduleId, 2, (completed, total) => onProgress(completed, total))
    .subscribe((result) => log.debug(`cached ${moduleId}: ${result.cards.length} cards, ${result.resources.length} resources`))
  }


  //-----------------------------------------------------------------------------------------------------
  //  fetching operations
  //-----------------------------------------------------------------------------------------------------

  fetchCardsByRequest(options: CardRequest): Observable<FrontendCard[]> {
    if (options.cardCount === 0) { return of([]) }
    options.maxCardCount = Math.max(1, options.maxCardCount ?? 10)
    options.cardCount = Math.min(options.maxCardCount, Math.max(1, options.cardCount ?? 1))

    return this._requestCards(options).pipe(map((cs) => toNonNullableArray(cs.map((c) => this._upsert(c)))))
  }

  private _requestCards(request: CardRequest, uid = this.userService.uid) {
    if (!uid) { return of([]) }
    request.maxCardCount = Math.max(1, request.maxCardCount ?? 10)
    request.cardCount = Math.min(request.maxCardCount, Math.max(1, request.cardCount ?? 1))

    const refCards = collection(this.firestore, `${CardTemplate.collectionPath()}`)
    const refStats = collection(this.firestore, `${CardStats.collectionPath(uid, request.moduleId)}`)

    let source: Observable<{ card: FrontendCard | CardTemplate | undefined, index: number, count: number }>

    switch (request.studyPhase) {
      case StudyPhase.Acquisition: source = this._handleAcquisitionPhase(request, refCards, refStats); break
      case StudyPhase.Review: source = this._handleReviewPhase(request, refStats); break
      case StudyPhase.Quiz: source = this._handleQuizPhase(request, refCards); break
      case StudyPhase.Practice:
      default: source = this._handlePracticePhase(request, refCards); break
    }
    return source.pipe(
      takeWhile((value) => value.index + 1 < value.count, true),
      map((value) => value.card),
      toArray()
    )
  }

  private _handleAcquisitionPhase(request: CardRequest, refCards: CollectionReference, refStats: CollectionReference) {
    const acquisitionCardIds = this._getCardIds(request, false);
    const cardIds = acquisitionCardIds.length > 0 ? acquisitionCardIds : this._getCardIds(request, true);
    const queries = (cardIds.length > 0)
      ? chunk(cardIds, 30).map((ids) => query(refCards, where(documentId(), 'in', ids)))
      : [query(refStats, where('score', '>=', 1), limit(request.maxCardCount))]
    return (cardIds.length > 0)
    ? this._executeCardQueries(queries, request, refStats, cardIds)
    : this._executeStatQueries(queries, request)
  }

  private _handleReviewPhase(request: CardRequest, refStats: CollectionReference) {
    const cardIds = request.reviewCardIds??[]
    const queries = cardIds.length > 0
      ? chunk(cardIds, 30).map((ids) => query(refStats, where(documentId(), 'in', ids)))
      : [query(refStats, where('reviewDueDate', '<=', request.afterReviewDueDateString ?? new DateOnly(request.planDate).toTimestamp()), limit(request.maxCardCount))]
    return this._executeStatQueries(queries, request);
  }

  private _handleQuizPhase(request: CardRequest, refCards: CollectionReference) {
    const random = getRandomInt(0, UINT16_MAX);
    const queries = [
      query(refCards, where('moduleId', '==', request.moduleId), where('random', '>=', random), orderBy('random', 'asc'), limit(request.cardCount)),
      query(refCards, where('moduleId', '==', request.moduleId), where('random', '<', random), orderBy('random', 'asc'), limit(request.cardCount))
    ];
    return this._fetchAndProcessCards(queries, request)
  }

  private _handlePracticePhase(request: CardRequest, refCards: CollectionReference) {
    const queries = [query(refCards, where('moduleId', '==', request.moduleId), orderBy(documentId(), 'asc'), ...(request.afterCardId ? [startAfter(request.afterCardId)] : []), limit(request.cardCount))]
    return this._fetchAndProcessCards(queries, request)
  }

  /**
   * Executes multiple Firestore queries, processes results into front end card entities.
   * Falls back to local cache if offline.
   *
   * @param queries - Firestore `Query` objects to execute.
   * @param request - `CardRequest` with moduleId, studyPhase, etc.
   * @param refStats - Firestore `CollectionReference` for user/module stats.
   * @param cardIds - Array of card IDs for filtering/sorting.
   * @returns `Observable` emitting processed card objects.
   */
  private _executeCardQueries(queries: Query[], request: CardRequest, refStats: CollectionReference, cardIds: string[]) {
    return !navigator.onLine
      ? this._fromCache(request, cardIds)
      : combineLatest(queries.map((q) => collectionSnapshots(q).pipe(
          catchError((val: FirebaseError) => { log.debug(val.stack, q); return of() })
        ))).pipe(
          map((results) => results.flat()), // Flatten the array of arrays into a single array
          takeWhile((items) => items.length > 0),
          mapToEntities<CardTemplate>(),
          mergeMap((items) => sortByArray<CardTemplate, string>(items, cardIds, (card) => card.moduleId).map((item, i) => ({ template: item, index: i, count: items.length }))),
          mergeMap(({ template, index, count }) => this._getCardForTemplate(refStats, template, index, count))
        )
  }
  /**
   * Executes multiple Firestore queries, processes results into front end card entities.
   * Falls back to local cache if offline.
   *
   * @param query - Firestore `Query` to execute.
   * @param request - `CardRequest` with moduleId, studyPhase, etc.
   * @returns `Observable` emitting processed card objects.
   */
  private _executeStatQueries(queries: Query[], request: CardRequest) {
    return !navigator.onLine
      ? this._fromCache(request, [])
      : combineLatest(queries.map((q) => collectionSnapshots(q).pipe(
          catchError((val: FirebaseError) => { log.debug(val.stack, q); return of([]) })
        ))).pipe(
          map((results) => results.flat()), // Flatten the array of arrays into a single array
          takeWhile((items) => items.length > 0),
          map((items) => request.startReviewDueDateString ? items : items.filter((item) => item['data']()['maturityPoints'] !== FrontendCard.MATURITY_BOUND)),
          mergeMap((stats) => stats.length === 0 ? [] : stats.map((s, i) => ({ stat: CardStats.converter.fromFirestore(s), index: i, count: stats.length }))),
          mergeMap(({ stat, index, count }) => this._getCardForStat(stat, index, count))
        )
  }
  /**
   * Executes multiple Firestore queries, processes results into card entities.
   * Falls back to local cache if offline.
   *
   * @param query - Firestore `Query` to execute.
   * @param request - `CardRequest` with moduleId, studyPhase, etc.
   * @returns `Observable` emitting processed card objects.
   */
  private _fetchAndProcessCards(queries: Query[], request: CardRequest) {
    return !navigator.onLine
      ? from(this.getCachedCards(request).map((c, index, items) => ({ card: c, index, count: items.length })))
      : merge(queries.map((q) => collectionSnapshots(q).pipe(
          catchError((val: FirebaseError) => { log.debug(val.stack, q); return of() })
        ))).pipe(
          mergeMap((results) => results, 5),
          takeWhile((items) => items.length > 0),
          mapToEntities<CardTemplate>(),
          mergeMap((items) => items.map((item, i) => ({ card: item, index: i, count: items.length })))
        )
  }

  private _fromCache(request: CardRequest, ids: string[] = []) {
    return from(
      this.getCachedCards(request, { ids }).map((c, index, items) => ({
        card: c,
        index,
        count: items.length
      }))
    )
  }

  private _getCardForTemplate(
    ref: CollectionReference,
    item: FrontendCard | CardTemplate,
    index: number,
    count: number,
    uid = this.userService.uid
  ) {
    if (!uid) { return [] }
    return collectionSnapshots(query(ref, where(documentId(), '==', item.id), limit(1))).pipe(
      map((stats) => stats[0] ? CardStats.converter.fromFirestore(stats[0]) : undefined),
      map((stat) => ({ card: this._createFrontendCard(item instanceof FrontendCard ? item.template : item, stat), index, count })),
    )
  }

  private _getCardForStat(
    stat: CardStats,
    index: number,
    count: number,
    uid = this.userService.uid
  ) {
    if (!uid) { return [] }
    return (
      docData(doc(this.firestore, `${CardTemplate.collectionPath()}/${stat.cardId}`)) as Observable<CardTemplate>).pipe(
      map((template) => ({ card: this._createFrontendCard({ ...(template??{}), id: stat.cardId as string }, stat), index, count })),
    )
  }

  private _getCardIds(options: CardRequest, complementary = false): string[] {
    if (!options.studyOrderProgress) { return [] }
    return complementary
      ? options.studyOrderProgress.findNextComplementaryCardIds(options)
      : options.studyOrderProgress.findNextAcquisitionCardIds(options)
  }

  // generic card fetcher for non study play

  fetchFrontendCards({
    ids,
    moduleId = '',
    templateOnly = false,
    uid = this.userService.uid,
    chunkSize = 5,
    demo = false
  }: {
    ids?: string | string[]
    moduleId?: string
    templateOnly?: boolean
    uid?: string | undefined,
    chunkSize?: number,
    demo?: boolean,
  }): Observable<FrontendCard[]> {
    if (!uid || (!ids && moduleId.length === 0)) { return of([]) }

    chunkSize = Math.min(chunkSize, 30) // firebase limit chunk size to 30
    ids = ids ? toNonNullableArray(ids).sort() : []
    const refCards = collection(this.firestore, `${CardTemplate.collectionPath()}`)
    const refStats = moduleId && !templateOnly ? collection(this.firestore, `${CardStats.collectionPath(uid, moduleId)}`) : undefined
    const queries: Query[] = []

    if (moduleId.length > 0) {
      const random = getRandomInt(0, UINT16_MAX);
      const constraints = [where('moduleId', '==', moduleId), ...(demo ? [where('moduleId', '==', moduleId), where('random', '>=', random), limit(20)] : [])]
      queries.push(query(refCards, ...constraints))
    } else if (ids.length > 1) {
      chunk(ids, chunkSize).forEach(chunkIds => queries.push(query(refCards, where(documentId(), 'in', chunkIds))))
    } else if (ids.length === 1) {
      const searchTerm = ids[0].trim()
      const endCode = searchTerm.slice(0, -1) + String.fromCharCode(searchTerm.charCodeAt(searchTerm.length - 1) + 1)
      queries.push(query(refCards, where(documentId(), '>=', searchTerm), where(documentId(), '<', endCode)))
    }

    return combineLatest(queries.map(q => collectionSnapshots(q))).pipe(
      catchError((error: FirebaseError) => { log.debug(error.stack); return of([]) }),
      map(snapshotArrays => snapshotArrays.flat()),
      mapToEntities<CardTemplate>(),
      switchMap(snapshotArrays => {
        const allCards = snapshotArrays.flat();
        if (allCards.length === 0) { return of([]) }
        if (!refStats) {
          return from(allCards).pipe(
            map(card => this._createFrontendCard(card)),
            toArray(),
            map(cs => toNonNullableArray(cs))
          )
        } else {
          return from(allCards).pipe(
            mergeMap((card, index) => this._getCardForTemplate(refStats, card, index, allCards.length)),
            takeWhile(({ index, count }) => index + 1 < count, true),
            map(({ card }) => this._upsert(card)),
            toArray(),
            map(cs => toNonNullableArray(cs))
          )
        }
      })
    )
  }

  private _createFrontendCard(template: CardTemplate|undefined, stats?: CardStats): FrontendCard | undefined {
    if (!template) { return undefined}
    try {
      const frontendTemplate = new FrontendCardTemplate(template)
      return new FrontendCard(frontendTemplate, stats)
    } catch (error) {
      log.debug(`Invalid card template: ${template.id}`, error)
      return undefined
    }
  }

  getCardCount(moduleId: string): number {
    return this.filterByModuleId(this.cards, moduleId).length
  }

  filterByModuleId(cards: FrontendCard[], moduleId: string) {
    return cards.filter((c) => c.moduleId === moduleId)
  }

  filterByName(cards: CardTemplate[], names: string[]): CardTemplate[] {
    return cards.filter((c) => names.includes(c.id))
  }

  sortByName(cards: CardTemplate[]) {
    return cards.sort((a, b) => (a.id > b.id ? 1 : -1))
  }

  getByName(name: string) {
    return this._cardsMap.get(name)
  }

  assetize(cards: FrontendCard[] | FrontendCardTemplate[], andFetch = false, concurrent?:number, onProgress?: ((completed: number, total: number) => void)) {
    return from(this.assetizeCards(cards, andFetch, concurrent, onProgress))
  }

  save(card: CardTemplate | CardTemplate[]) {
    return Promise.resolve(toNonNullableArray(card))
  }

  push?(): Observable<CardTemplate[]> {
    throw new Error('Method not implemented.')
  }

  createUserResponse(
    userId: string,
    cardId: string,
    responseTime: number,
    isAnswer: boolean,
    studyPhase: StudyPhase
  ) {
    return new UserResponse(
      cardId,
      userId,
      Timestamp.now(),
      responseTime,
      isAnswer,
      studyPhase
    )
  }

  // Backend to Frontend card utils

  toFrontendCard(c: FrontendCard | CardTemplate) {
    const template = c instanceof FrontendCard ? c.template : new FrontendCardTemplate(c)
    const stats = c instanceof FrontendCard ? c.stats : undefined
    return new FrontendCard(template, stats)
  }

  async assetizeCards(
    cards: FrontendCard[] | FrontendCardTemplate[],
    andFetch = false,
    concurrent?: number,
    onProgress?: ((completed: number, total: number) => void)
  ) {
    const allResources = cards.flatMap((c) => (c instanceof FrontendCard ? c.template : c).allChoices?.flatMap((c: Choice) => c.resources))

    // Track progress across chunks
    let globalCompleted = 0;
    const totalResources = allResources.length;
    onProgress?.(0, totalResources);

    const chunkSize = 5;
    const resourceChunks = chunk(allResources, chunkSize);

    for (const [index, chunk] of resourceChunks.entries()) {
      await this.storageService.assetizeResources(
        chunk,
        andFetch,
        concurrent,
        (chunkCompleted) => {
          globalCompleted = (index * chunkSize) + chunkCompleted;
          onProgress?.(globalCompleted, totalResources)
        }
      )
    }

    return { cards, resources: allResources };
  }

  validate(card: FrontendCard | undefined) {
    if (!card?.template?.cardID) { return }
    return new FrontendCard(card.template, card.stats)
  }
/*
  private _toBackendRequest(options: CardRequest) {
    let request:
      | AcquisitionCardRetrievalRequest
      | ReviewCardRetrievalRequest
      | PracticeCardRetrievalRequest
    switch (options.studyPhase) {
      case StudyPhase.Acquisition:
        {
          request = {
            studyPhase: options.studyPhase,
            moduleId: options.moduleId,
            cardCount: options.cardCount,
            acquisitionCardDeck: options.acquisitionCardDeck,
            justAcquiredCardId: options.justAcquiredCardId
          }
        }
        break
      case StudyPhase.Review:
        {
          request = {
            studyPhase: options.studyPhase,
            moduleId: options.moduleId,
            cardCount: options.cardCount,
            startReviewDueDateString: options.startReviewDueDateString,
            afterReviewDueDateString: options.afterReviewDueDateString
          }
        }
        break
      case StudyPhase.Practice:
      default:
        {
          request = {
            studyPhase: options.studyPhase,
            moduleId: options.moduleId,
            cardCount: options.cardCount,
            afterCardId: options.afterCardId
          }
        }
        break
    }
    return request
  }
*/
}
